import { useEffect, useState } from "react";
import Icon from "../components/Icon";
import StatusPill from "../components/StatusPill";

function HomePage({ stats = [], inventoryStats = [], inventorySnapshot = {}, invoiceSnapshot = {}, alerts = [], activityRows = [] }) {
  const [activityCollapsed, setActivityCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 820;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setActivityCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const statCards = stats?.length ? stats : [];
  const inventoryCards = inventoryStats?.length ? inventoryStats : [];
  const invoiceSummary = invoiceSnapshot.summary || [];
  const upcomingInvoices = invoiceSnapshot.upcoming || [];
  const invoiceTotals = invoiceSnapshot.totals || {};
  const alertItems = alerts || [];
  const activityCount = activityRows.length;
  const parseCount = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const cleaned = value.replace(/[^\d.-]/g, "");
      const parsed = Number.parseInt(cleaned, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };
  const pipelineTotal = invoiceSummary.reduce((total, item) => total + parseCount(item.value), 0);
  const invoiceBreakdown = invoiceSummary.map((item) => {
    const numericValue = parseCount(item.value);
    const percent = pipelineTotal ? Math.round((numericValue / pipelineTotal) * 100) : 0;
    const slug = typeof item.label === "string" ? item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "";
    return {
      ...item,
      numericValue,
      percent,
      slug,
    };
  });

  const renderStatusPill = (status) => {
    if (status?.type === "status") {
      return <StatusPill label={status.label} tone={status.tone} />;
    }
    return status ? <span className="muted small">{status}</span> : null;
  };

  return (
    <>
      {inventoryCards.length ? (
        <section className="inventory-insights home-inventory-insights" aria-label="Inventory overview">
          <div className="inventory-insights-head">
            <div>
              <h2>Inventory overview</h2>
              <p className="muted small">Live stats from the catalog.</p>
            </div>
          </div>
          <div className="inventory-insight-grid">
            {inventoryCards.map((card) => {
              const pills = card.pills || [];
              const hasPills = pills.length > 0;
              const hasMeta = Boolean(card.meta);
              const showPillRow = hasPills || hasMeta;

              return (
                <article key={card.key} className={`insight-card ${card.tone || ""}`}>
                  <div className="insight-label">{card.label}</div>
                  <div className="insight-value-row">
                    <div className="insight-value">{card.value}</div>
                  </div>
                  <div className={`insight-pills ${showPillRow ? "" : "empty"}`}>
                    {hasMeta ? <span className="pill meta-pill">{card.meta}</span> : null}
                    {hasPills
                      ? pills.map((pill) => (
                          <span key={pill} className="pill">
                            {pill}
                          </span>
                        ))
                      : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="stat-grid home-stats">
        {statCards.map((card) => (
          <article key={card.key} className={`stat-card ${card.tone || ""}`}>
            <div className="stat-label">{card.label}</div>
            <div className="stat-value">{card.value}</div>
            {card.hint ? <div className={`stat-hint ${card.hintTone || ""}`}>{card.hint}</div> : null}
          </article>
        ))}
      </section>

      <div className="home-stack">
        <div className="home-grid">
          <section className="panel home-panel">
            <header className="panel-header">
              <div className="panel-title">
                <span className="panel-icon">
                  <Icon name="bell" />
                </span>
                <div>
                  <h2>Alerts</h2>
                  <p className="muted">Clear these items to keep operations humming.</p>
                </div>
              </div>
            </header>
            <ul className="home-alert-list">
              {alertItems.length ? (
                alertItems.map((alert) => (
                  <li key={alert.id} className={`home-alert ${alert.tone || ""}`}>
                    <span className="home-alert-indicator" aria-hidden="true" />
                    <div className="home-alert-content">
                      <div className="home-alert-copy">
                        <p className="home-alert-title">{alert.title}</p>
                        <p className="home-alert-desc">{alert.description}</p>
                      </div>
                      {alert.meta ? <span className="home-alert-meta">{alert.meta}</span> : null}
                    </div>
                  </li>
                ))
              ) : (
                <li className="home-alert empty">
                  <p className="home-alert-title">All clear</p>
                  <p className="home-alert-desc">There are no pending alerts right now.</p>
                </li>
              )}
            </ul>
          </section>
        </div>

        <div className="home-grid">
          <section className="panel home-panel">
            <header className="panel-header">
              <div className="panel-title">
                <span className="panel-icon">
                  <Icon name="file" />
                </span>
                <div>
                  <h2>Invoice pipeline</h2>
                  <p className="muted">Track cash flow before diving into the invoices tab.</p>
                </div>
              </div>
            </header>

            <div className="invoice-pipeline">
              <div className="invoice-pipeline__grid">
                <div className="invoice-pipeline__column">
                  <div className="invoice-totals">
                    <article className="pipeline-card">
                      <div className="muted small">Outstanding</div>
                      <div className="invoice-total-value">{invoiceTotals.outstanding || "KWD 0.000"}</div>
                      <p className="muted extra-small">Awaiting settlement</p>
                    </article>
                    <article className="pipeline-card danger-card">
                      <div className="muted small">Overdue</div>
                      <div className="invoice-total-value danger">{invoiceTotals.overdue || "KWD 0.000"}</div>
                      <p className="muted extra-small">Needs immediate follow up</p>
                    </article>
                  </div>

                  <div className="pipeline-breakdown">
                    <div className="pipeline-breakdown__head">
                      <div>
                        <div className="muted small">Pipeline breakdown</div>
                        <h3>Invoice statuses</h3>
                      </div>
                      <span className="muted small">{pipelineTotal ? `${pipelineTotal} invoices` : "No invoices yet"}</span>
                    </div>

                    {invoiceBreakdown.length ? (
                      <ul className="pipeline-stage-list">
                        {invoiceBreakdown.map((item) => (
                          <li key={item.label}>
                            <div className="stage-label-row">
                              <span className="stage-label">{item.label}</span>
                              <span className="stage-value">{item.value}</span>
                            </div>
                            <div className="stage-bar" aria-hidden="true">
                              <div className={`stage-bar__fill ${item.slug ? `stage-${item.slug}` : ""}`} style={{ width: `${item.percent}%` }} />
                            </div>
                            <span className="stage-percent muted extra-small">{item.percent}% of pipeline</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="muted small">No invoices in the pipeline yet.</p>
                    )}
                  </div>
                </div>

                <div className="invoice-pipeline__column invoice-pipeline__column--upcoming">
                  <div className="pipeline-breakdown__head">
                    <div>
                      <div className="muted small">Upcoming due</div>
                      <h3>Next 5 invoices</h3>
                    </div>
                    {upcomingInvoices.length ? <span className="muted small">Sorted by due date</span> : null}
                  </div>

                  {upcomingInvoices.length ? (
                    <ul className="pipeline-upcoming-list">
                      {upcomingInvoices.map((row) => (
                        <li key={row.id} className="pipeline-upcoming-item">
                          <div>
                            <div className="mini-title">{row.customer}</div>
                            <div className="mini-subtitle">
                              {row.invoiceNumber} · Due {row.dueDate}
                            </div>
                          </div>
                          <div className="mini-meta">
                            <strong>{row.amount}</strong>
                            {renderStatusPill(row.status)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="pipeline-empty">
                      <p className="muted small">No upcoming invoices awaiting payment.</p>
                      <p className="muted extra-small">New invoices will appear here automatically.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

        </div>

        <section className={`panel home-panel wide activity-panel ${activityCollapsed ? "collapsed" : ""}`}>
          <header className="panel-header activity-panel__head">
            <div className="panel-title">
              <span className="panel-icon">
                <Icon name="home" />
              </span>
              <div>
                <h2>Activity</h2>
                <p className="muted">Audit trail of user activity across the workspace.</p>
              </div>
            </div>
            <span className="activity-count muted extra-small">{activityCount ? `${activityCount} recent` : "No activity yet"}</span>
            <button
              type="button"
              className="collapse-toggle"
              aria-expanded={!activityCollapsed}
              onClick={() => setActivityCollapsed((prev) => !prev)}
            >
              <Icon name={activityCollapsed ? "chevronRight" : "chevronDown"} />
              <span>{activityCollapsed ? "Show activity" : "Hide activity"}</span>
            </button>
          </header>

          {!activityCollapsed ? (
            <div className="activity-log">
              {activityRows.length ? (
                activityRows.map((log) => {
                  const detail = [log.detail, log.timestamp].filter(Boolean).join(" · ");
                  return (
                    <div key={log.id} className="activity-entry">
                      <div className="activity-entry-body">
                        <div className="activity-entry-title">{log.message}</div>
                        {detail ? <div className="activity-entry-detail">{detail}</div> : null}
                      </div>
                      <div className="activity-entry-meta">
                        {log.meta ? <span className="pill">{log.meta}</span> : null}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="muted small">No user activity recorded yet.</p>
              )}
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}

export default HomePage;
